// src/pages/Settings.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  FiSave,
  FiLock,
  FiSmartphone,
  FiShield,
  FiRefreshCw,
  FiToggleRight,
  FiX,
  FiKey
} from 'react-icons/fi';
import Cookies from "js-cookie";

export default function Settings() {
  // — Passcode gating —
  const STATIC_PASSCODE = '6363';
  const [passcodeVerified, setPasscodeVerified] = useState(false);
  const [passcode, setPasscode] = useState(['', '', '', '']);
  const [passcodeError, setPasscodeError] = useState('');

  const token = Cookies.get("authToken");

  // Redirect if not logged in
  useEffect(() => {
    if (!token) window.location.href = "/";
  }, [token]);

  const handlePasscodeChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...passcode];
    next[idx] = val.slice(-1);
    setPasscode(next);
    setPasscodeError('');

    if (val && idx < 3) {
      document.getElementById(`pin-${idx + 1}`)?.focus();
    }
  };

  const handlePasscodeKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !passcode[idx] && idx > 0) {
      document.getElementById(`pin-${idx - 1}`)?.focus();
    }
  };

  useEffect(() => {
    if (passcode.every(d => d !== '') && !passcodeVerified) {
      const code = passcode.join('');
      if (code === STATIC_PASSCODE) {
        setPasscodeVerified(true);
      } else {
        setPasscodeError('Invalid passcode');
        setPasscode(['', '', '', '']);
        document.getElementById('pin-0')?.focus();
      }
    }
  }, [passcode, passcodeVerified]);

  // — Settings logic (only runs after passcode verified) —
  const [fullSettings, setFullSettings] = useState(null);
  const [formData, setFormData] = useState({
    whatsappApiUrl: '',
    phoneNumberId: '',
    accessToken: '',
    businessAccountId: '',
    retryAttempts: 1,
    demoMode: 'false',
    demoKafka: 'false',
    demoWhatsapp: 'false'
  });
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // FIXED: Safe API fetch with useCallback
  const fetchSettings = useCallback(() => {
    if (!token || !passcodeVerified) return;

    fetch('/api/api/settings', {
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res =>
        res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))
      )
      .then(data => {
        if (!data.success) throw new Error('API error');
        const s = data.settings;
        setFullSettings(s);
        setFormData({
          whatsappApiUrl: s.WHATSAPP_API_URL || '',
          phoneNumberId: s.WHATSAPP_PHONE_NUMBER_ID || '',
          accessToken: s.WHATSAPP_ACCESS_TOKEN || '',
          businessAccountId: s.WHATSAPP_BUSINESS_ACCOUNT_ID || '',
          retryAttempts: Number(s.RETRY_MAX_ATTEMPTS) || 1,
          demoMode: String(s.DEMO_MODE),
          demoKafka: String(s.DEMO_KAFKA),
          demoWhatsapp: String(s.DEMO_WHATSAPP)
        });
      })
      .catch(err => {
        console.error(err);
        setError(err.message);
      });
  }, [token, passcodeVerified]);

  // FIXED: Now dependency is safe
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(fd => ({ ...fd, [name]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!fullSettings) return;

    const payload = {
      KAFKA_BOOTSTRAP_SERVERS: fullSettings.KAFKA_BOOTSTRAP_SERVERS,
      KAFKA_TOPIC: fullSettings.KAFKA_TOPIC,
      KAFKA_GROUP_ID: fullSettings.KAFKA_GROUP_ID,
      KAFKA_AUTO_OFFSET_RESET: fullSettings.KAFKA_AUTO_OFFSET_RESET,
      WHATSAPP_API_URL: formData.whatsappApiUrl,
      WHATSAPP_PHONE_NUMBER_ID: formData.phoneNumberId,
      WHATSAPP_ACCESS_TOKEN: formData.accessToken,
      WHATSAPP_BUSINESS_ACCOUNT_ID: formData.businessAccountId,
      RETRY_MAX_ATTEMPTS: formData.retryAttempts,
      RETRY_BACKOFF_FACTOR: parseFloat(fullSettings.RETRY_BACKOFF_FACTOR),
      LOG_LEVEL: fullSettings.LOG_LEVEL,
      DEMO_MODE: formData.demoMode === 'true',
      DEMO_KAFKA: formData.demoKafka === 'true',
      DEMO_WHATSAPP: formData.demoWhatsapp === 'true'
    };

    fetch('/api/api/settings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    })
      .then(res =>
        res.ok
          ? res.json()
          : res.json().then(err =>
              Promise.reject(new Error(err.detail || JSON.stringify(err)))
            )
      )
      .then(d => {
        if (d.success) {
          setShowSuccessModal(true);
        } else {
          setError(d.message || 'Unknown error saving settings');
        }
      })
      .catch(err => setError(err.message));
  };

  // PASSCODE SCREEN
  if (!passcodeVerified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <FiKey className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Enter Security Passcode
            </h1>
            <p className="text-gray-600">Please enter the 4-digit code</p>
          </div>

          <div className="flex justify-center gap-3 mb-4">
            {[0, 1, 2, 3].map(i => (
              <input
                key={i}
                id={`pin-${i}`}
                type="password"
                maxLength="1"
                value={passcode[i]}
                onChange={e => handlePasscodeChange(i, e.target.value)}
                onKeyDown={e => handlePasscodeKeyDown(i, e)}
                className="w-16 h-16 text-3xl text-center rounded-lg border-2 border-gray-200 
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                autoFocus={i === 0}
                inputMode="numeric"
              />
            ))}
          </div>

          {passcodeError && (
            <p className="text-center text-red-600 text-sm mb-4">
              {passcodeError}
            </p>
          )}

          <p className="text-center text-sm text-gray-600">
            Contact your administrator if you've forgotten the code.
          </p>
        </div>
      </div>
    );
  }

  // SETTINGS SCREEN
  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mx-auto max-w-full">
          <div className="bg-white rounded-xl shadow-sm p-6">

            <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <FiShield className="w-6 h-6" style={{ color: '#581745' }} />
              Application Settings
            </h1>

            {error && <div className="mb-4 text-red-600">Error: {error}</div>}

            {!fullSettings ? (
              <div className="py-10 text-center text-gray-600">Loading settings…</div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* WhatsApp Business API */}
                <div className="border-b pb-6 border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiSmartphone className="w-5 h-5 text-gray-600" />
                    WhatsApp Business API
                  </h2>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                    <div className="lg:col-span-2 space-y-2">
                      <label className="text-sm font-medium text-gray-700">API Base URL</label>
                      <input
                        type="url"
                        name="whatsappApiUrl"
                        value={formData.whatsappApiUrl}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Phone Number ID</label>
                      <input
                        type="text"
                        name="phoneNumberId"
                        value={formData.phoneNumberId}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200"
                        required
                      />
                    </div>

                    <div className="lg:col-span-3 space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <FiLock className="w-4 h-4" /> Access Token
                      </label>
                      <input
                        type="password"
                        name="accessToken"
                        value={formData.accessToken}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200"
                        required
                      />
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="text-sm font-medium text-gray-700">Business Account ID</label>
                      <input
                        type="text"
                        name="businessAccountId"
                        value={formData.businessAccountId}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Application Settings */}
                <div className="border-b pb-6 border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiRefreshCw className="w-5 h-5 text-gray-600" />
                    Application Settings
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Max Retry Attempts</label>
                      <input
                        type="number"
                        name="retryAttempts"
                        value={formData.retryAttempts}
                        onChange={handleChange}
                        min="1"
                        max="10"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Demo Mode Flags */}
                <div className="pb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiToggleRight className="w-5 h-5 text-gray-600" />
                    Demo Mode Flags
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {['demoMode', 'demoKafka', 'demoWhatsapp'].map(key => (
                      <div key={key} className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          {key === 'demoMode'
                            ? 'Demo Mode'
                            : key === 'demoKafka'
                            ? 'Demo Kafka'
                            : 'Demo WhatsApp'}
                        </label>
                        <select
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-200"
                        >
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    className="px-6 py-2 rounded-lg flex items-center gap-2"
                    style={{ backgroundColor: '#581745', color: 'white' }}
                  >
                    <FiSave className="w-4 h-4" /> Save Settings
                  </button>
                </div>

              </form>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Settings Saved</h3>
              <button onClick={() => setShowSuccessModal(false)}>
                <FiX className="w-6 h-6 text-gray-500 hover:text-gray-800" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-800">Settings saved successfully.</p>
            </div>

            <div className="flex justify-end px-6 py-4 border-t">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
